/**
 * Recurring Invoices — list of clients with active recurring invoices.
 * Click a client to expand and see every recurring template for them
 * (amount, cadence, next run date, description).
 *
 * Backed by `GET /api/invoices/recurring` (already returns all
 * recurring templates) + `/api/clients` for the display name lookup.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Repeat, Search, Loader2, ChevronDown, ChevronRight,
  Calendar, DollarSign, Users, ExternalLink,
} from "lucide-react";

const fmtAUD = (v) => new Intl.NumberFormat("en-AU", {
  style: "currency", currency: "AUD", maximumFractionDigits: 2,
}).format(Number(v) || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU", {
    day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
};

const CADENCE_LABEL = {
  weekly: "Weekly", fortnightly: "Fortnightly", monthly: "Monthly",
  quarterly: "Quarterly", annual: "Annual", biennial: "Biennial",
};

export default function RecurringInvoicesPage() {
  const [recs, setRecs] = useState([]);
  const [clients, setClients] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [r, c] = await Promise.all([
          api.get("/invoices/recurring"),
          api.get("/clients"),
        ]);
        setRecs(Array.isArray(r.data) ? r.data : (r.data?.invoices || []));
        const map = {};
        (c.data?.clients || c.data || []).forEach((cl) => {
          map[cl.client_id] = cl.company_name || cl.name || cl.client_id;
        });
        setClients(map);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load recurring invoices");
      } finally { setLoading(false); }
    })();
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    for (const r of recs) {
      const key = r.client_id || "unassigned";
      if (!map[key]) {
        map[key] = {
          client_id: r.client_id,
          client_name: clients[r.client_id] || r.client_id || "Unassigned",
          templates: [],
          monthly_total: 0,
          annual_total: 0,
        };
      }
      const amt = Number(r.total_ex_gst ?? r.subtotal ?? r.total ?? 0);
      const monthlyFactor = { weekly: 4.333, fortnightly: 2.167, monthly: 1,
        quarterly: 1 / 3, annual: 1 / 12, biennial: 1 / 24 }[r.cadence] || 1;
      map[key].templates.push(r);
      map[key].monthly_total += amt * monthlyFactor;
      map[key].annual_total += amt * monthlyFactor * 12;
    }
    let arr = Object.values(map);
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((g) =>
        g.client_name.toLowerCase().includes(q) ||
        g.templates.some((t) =>
          (t.name || "").toLowerCase().includes(q) ||
          (t.description || "").toLowerCase().includes(q)));
    }
    arr.sort((a, b) => b.annual_total - a.annual_total);
    return arr;
  }, [recs, clients, search]);

  const totals = useMemo(() => ({
    clients: grouped.length,
    templates: grouped.reduce((a, g) => a + g.templates.length, 0),
    monthly: grouped.reduce((a, g) => a + g.monthly_total, 0),
    annual: grouped.reduce((a, g) => a + g.annual_total, 0),
  }), [grouped]);

  return (
    <div className="p-6 space-y-6 max-w-6xl" data-testid="recurring-invoices-page">
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-gradient-to-br from-sky-500 to-indigo-600 text-white">
          <Repeat className="h-6 w-6" />
        </div>
        <div>
          <div className="label-overline">Invoicing &amp; Receivables</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">
            Recurring Invoices
          </h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Every client with an active recurring invoice template. Click
            a client to expand and see the underlying schedules,
            amounts, and next-run dates.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Clients" value={totals.clients} />
        <StatCard icon={Repeat} label="Active templates" value={totals.templates} />
        <StatCard icon={DollarSign} label="Monthly (ex GST)" value={fmtAUD(totals.monthly)} accent="emerald" />
        <StatCard icon={DollarSign} label="Annualised (ex GST)" value={fmtAUD(totals.annual)} accent="emerald" />
      </div>

      <div className="max-w-md">
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by client or template…"
            className="rounded-sm pl-8 h-9"
            data-testid="recurring-invoices-search" />
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center">
          <Loader2 className="h-6 w-6 animate-spin inline text-zinc-400" />
        </div>
      ) : grouped.length === 0 ? (
        <Card className="rounded-sm"><CardContent className="p-8 text-center text-sm text-zinc-500">
          No recurring invoice templates yet.
        </CardContent></Card>
      ) : (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-0">
            {grouped.map((g) => {
              const isOpen = !!expanded[g.client_id || "unassigned"];
              return (
                <div key={g.client_id || "unassigned"}
                  className="border-b border-zinc-100 last:border-b-0"
                  data-testid={`recurring-invoices-client-${g.client_id || "unassigned"}`}>
                  <button type="button"
                    onClick={() => setExpanded((e) => ({
                      ...e, [g.client_id || "unassigned"]: !isOpen }))}
                    className="w-full text-left flex items-center gap-3 p-4 hover:bg-zinc-50">
                    {isOpen ? <ChevronDown className="h-4 w-4 text-zinc-500 shrink-0" />
                           : <ChevronRight className="h-4 w-4 text-zinc-500 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{g.client_name}</div>
                      <div className="text-[11px] text-zinc-500">
                        {g.templates.length} template{g.templates.length === 1 ? "" : "s"}
                      </div>
                    </div>
                    <div className="text-right text-xs shrink-0">
                      <div className="font-semibold text-emerald-700">
                        {fmtAUD(g.monthly_total)}<span className="text-[10px] text-zinc-400"> /mo</span>
                      </div>
                      <div className="text-[10px] text-zinc-500">
                        {fmtAUD(g.annual_total)} /yr
                      </div>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="bg-zinc-50 border-t border-zinc-100 divide-y divide-zinc-100">
                      {g.templates.map((t) => (
                        <div key={t.recurring_id}
                          className="p-3 grid grid-cols-1 md:grid-cols-5 gap-3 items-start"
                          data-testid={`recurring-invoice-template-${t.recurring_id}`}>
                          <div className="md:col-span-2">
                            <div className="text-sm font-medium">{t.name || "(untitled)"}</div>
                            {t.description && (
                              <div className="text-[11px] text-zinc-500 mt-0.5">
                                {t.description}
                              </div>
                            )}
                            {(t.line_items || []).length > 0 && (
                              <div className="text-[10px] text-zinc-400 mt-1">
                                {t.line_items.length} line item{t.line_items.length === 1 ? "" : "s"}
                              </div>
                            )}
                          </div>
                          <div>
                            <Badge variant="outline" className="rounded-sm bg-sky-50 text-sky-800 border-sky-200 text-[10px]">
                              {CADENCE_LABEL[t.cadence] || t.cadence || "monthly"}
                            </Badge>
                            {t.day_of_month != null && (
                              <div className="text-[10px] text-zinc-500 mt-1 flex items-center gap-1">
                                <Calendar className="h-2.5 w-2.5" />
                                Day {t.day_of_month} of each cycle
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-zinc-500">
                            <div className="label-overline">Next run</div>
                            <div className="text-[11px]">{fmtDate(t.next_run_date)}</div>
                            {t.last_generated_at && (
                              <div className="text-[9px] text-zinc-400 mt-0.5">
                                Last: {fmtDate(t.last_generated_at)}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-sm text-emerald-700">
                              {fmtAUD(t.total_ex_gst ?? t.subtotal ?? t.total)}
                            </div>
                            <div className="text-[10px] text-zinc-400">ex GST</div>
                            {g.client_id && (
                              <Link to={`/clients/${g.client_id}/invoicing`}
                                className="text-[10px] text-[#002FA7] hover:underline mt-1 inline-flex items-center gap-0.5">
                                Open <ExternalLink className="h-2.5 w-2.5" />
                              </Link>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <Card className="rounded-sm border-zinc-200">
      <CardContent className="p-3">
        <div className="label-overline flex items-center gap-1.5">
          <Icon className="h-3 w-3" />{label}
        </div>
        <div className={`text-2xl font-bold mt-1 ${accent === "emerald" ? "text-emerald-700" : ""}`}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
